<template>
  <section
    itemscope
    itemtype="http://schema.org/CreativeWork"
    class="learning-path"
  >
    <article class="learning-path__content">
      <template v-if="!!learningPath">
        <Overview
          :learningPath="learningPath"
          :certifications="certifications"
          :hasAccess="hasAccess"
          :isLabExperiencePath="isLabExperiencePath"
          :unSubscribedLink="unSubscribedLink"
          :nextContent="learningPathsNextContentCache[learningPath.id]"
          :rating="rating"
          :isRatingLoading="isRatingLoading"
          @rate-learning-path="openRatingModal"
          @openLevel="openLevel"
        />
      </template>
      <template v-else>
        <PathOverviewSkel />
      </template>
      <template v-if="!!learningPath && !localLoading">
        <ContentSection
          :learningPath="learningPath"
          :hasAccess="hasAccess"
          :unSubscribedLink="unSubscribedLink"
          :levelOpen="levelOpen"
          @openLevel="openLevel"
        />
      </template>
      <template v-else>
        <PathContentSkel />
      </template>
      <CertificationsSection
        v-if="displayCertificationsSection"
        :learningPath="learningPath"
        :certifications="certifications"
      />
      <RecommendedNextSection
        v-if="nextLearningPath"
        :nextLearningPath="nextLearningPath"
        :nextContent="learningPathsNextContentCache[nextLearningPath.id]"
      />
      <ExamScoreModal
        v-if="currentScoredAttempt && currentScoredAttempt.id"
        :attempt="currentScoredAttempt"
        @close-score-modal="closeScoreModal"
      />
    </article>
    <RatingModal
      v-if="isRatingModalOpen"
      :modelId="learningPath.id"
      :modelType="CONSTANTS.CONTENT_TYPES.LEARNING_PATH"
      :modelName="learningPath.name"
      :rating="rating"
      @close="closeRatingModal"
      @close-and-refresh="closeRatingModal(true)"
    />
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { hasBaseAccess } from '@/helpers'
import Overview from '@/components/learning-path/overview'
import PathOverviewSkel from '@/components/loaders/skeletons/learning-paths/PathOverviewSkel.vue'
import PathContentSkel from '@/components/loaders/skeletons/learning-paths/PathContentSkel.vue'
import RecommendedNextSection from '@/components/learning-path/recommended-next'
import ContentSection from '@/components/learning-path/content'
import CertificationsSection from '@/components/learning-path/certifications'
import ExamScoreModal from '@/components/modals/ExamScoreModal.vue'
import CONSTANTS from '@/constants'
import RatingModal from '@/components/modals/RatingModal.vue'

export default {
  name: 'LearningPath',
  components: {
    Overview,
    PathOverviewSkel,
    PathContentSkel,
    RecommendedNextSection,
    CertificationsSection,
    ExamScoreModal,
    ContentSection,
    RatingModal,
  },
  metaInfo() {
    const { learningPath } = this
    return {
      title:
        learningPath && learningPath.name
          ? `INE - ${learningPath.name}`
          : `INE - Learning Path`,
      meta: [
        {
          vmid: 'learning-path',
          name: `description`,
          content:
            learningPath && learningPath.description
              ? learningPath.description
              : '',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${CONSTANTS.MARKETING_WEBSITE_DIRECTORY_BASE_URL}/paths/${this.$route?.params?.slug}`,
        },
      ],
    }
  },
  data: () => {
    return {
      localLoading: true,
      rating: null,
      CONSTANTS,
      isRatingModalOpen: false,
      isRatingLoading: true,
      levelOpen: '/',
    }
  },
  computed: {
    ...mapState({
      isPageLoading: state => state.app.isPageLoading,
      learningPathsCache: state => state.learningPaths.learningPathsCache,
      certificationsCache: state => state.certifications.certificationsCache,
      learningPathsNextContentCache: state =>
        state.learningPaths.learningPathsNextContentCache,
      currentVideos: state => state.content.currentVideos,
      currentQuizzes: state => state.content.currentQuizzes,
      currentExercises: state => state.content.currentExercises,
      currentLabs: state => state.content.currentLabs,
      currentIFrames: state => state.content.currentIFrames,
      plansLink: state => state.app.plansLink,
      isAuthenticated: state => state.auth.isAuthenticated,
      currentScoredAttempt: state => state.certifications.currentScoredAttempt,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
    }),
    learningPath() {
      return this.learningPathsCache[this.$route.params.id]
    },
    isLabExperiencePath() {
      return Object.values(CONSTANTS.SPECIAL_LEARNING_PATHS).includes(
        this.learningPath?.id
      )
    },
    unSubscribedLink() {
      if (this.isLabExperiencePath) {
        return '//info.ine.com/lab-experience/'
      }
      return `//${this.plansLink}`
    },
    nextLearningPath() {
      return this.learningPathsCache[this.learningPath?.next_learning_path?.id]
    },
    hasAccess() {
      return (
        this.learningPath &&
        (hasBaseAccess(this.learningPath) ||
          (this.isAuthenticated && this.learningPath.has_sample_content))
      )
    },
    certificationsLoaded() {
      const certificationIds = this.learningPath?.certifications
      if (certificationIds && certificationIds.length) {
        return certificationIds.every(
          certId => this.certificationsCache[certId]
        )
      }

      return false
    },
    certifications() {
      return this.learningPath?.certifications?.map(
        certId => this.certificationsCache[certId]
      )
    },
    isCertificationsActive() {
      return process.env.VUE_APP_CERTIFICATIONS_ENABLED
    },
    displayCertificationsSection() {
      return (
        this.certificationsLoaded &&
        this.isCertificationsActive &&
        this.certifications &&
        this.certifications.length
      )
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.queryData(to.params.id)
      }
    },
  },
  async created() {
    await this.queryData(this.$route.params.id)
  },
  destroyed() {
    this.resetContentState()
  },
  methods: {
    ...mapActions({
      getLearningPathById: 'learningPaths/getLearningPathById',
      getCurrentLearningPath: 'learningPaths/getCurrentLearningPath',
      getNextContent: 'learningPaths/getNextContent',
      fetchContentForLearningPath: 'content/fetchContentForLearningPath',
      resetContentState: 'content/resetContentState',
      getCertificationsForLearningPath:
        'certifications/getCertificationsForLearningPath',
    }),
    ...mapActions('content', ['getContentRatingByModel']),
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    closeScoreModal() {
      this.$store.dispatch('certifications/setCurrentAttemptScored', {})
    },
    async fetchContent(payload) {
      this.localLoading = true
      await this.fetchContentForLearningPath(payload)
      this.localLoading = false
    },
    async fetchRating() {
      this.isRatingLoading = true

      if (this.learningPath?.id) {
        const { results } = await this.getContentRatingByModel({
          model_id: this.learningPath.id,
          model_type: CONSTANTS.CONTENT_TYPES.LEARNING_PATH,
        })

        if (results.length) this.rating = results[0]
      }

      this.isRatingLoading = false
    },
    openRatingModal() {
      this.isRatingModalOpen = true
    },
    closeRatingModal(needsRefresh) {
      this.isRatingModalOpen = false
      if (needsRefresh) this.fetchRating()
    },
    async queryData(pathId) {
      const dispatcher = []
      const learningPath = await this.getCurrentLearningPath(pathId)
      const nextPathid = learningPath.next_learning_path?.id

      const { http_code } = learningPath
      if (http_code && http_code === 404) {
        return this.$router.push({
          name: 'NotFound',
          params: { pathMatch: this.$route.path },
        })
      }

      dispatcher.push(
        this.fetchContent({
          learningPath,
          isAuthenticated: this.isAuthenticated,
        })
      )
      if (this.isAuthenticated) {
        dispatcher.push(this.fetchRating())
      }
      if (this.hasAccess) dispatcher.push(this.getNextContent(pathId))
      if (learningPath.certifications?.length) {
        dispatcher.push(this.getCertificationsForLearningPath({ pathId }))
      }
      if (nextPathid) {
        dispatcher.push(
          this.getLearningPathById(nextPathid),
          this.getCertificationsForLearningPath({
            pathId: nextPathid,
          })
        )
        if (this.hasAccess) dispatcher.push(this.getNextContent(nextPathid))
      }

      Promise.all(dispatcher)

      this.setParentContent({ ...learningPath, type: 'learning-path' })
    },
    openLevel(topicIndex, levelId) {
      this.levelOpen = `${topicIndex}/${levelId}`
    },
  },
}
</script>

<style lang="scss" scoped>
.learning-path {
  &__content {
    position: relative;
  }
}
</style>
